import { useEffect, useState, useCallback, useRef } from 'react';

import {
  getInventoryWarehouses,
  getInventoryWarehouseLocations,
  getInventoryLocationsForProduct,
} from '../../../store/inventory/inventory.api';
import { locationNameFromParts } from '../display-helpers';

/**
 * Hook for fetching inventory modal data (warehouses, locations, and product inventory).
 * Use this when you need inventory data outside of ProductDetailsContext.
 * @param {string} productId - The product ID to fetch inventory for
 * @returns {{
 *   isLoading: boolean,
 *   inventoryWarehouses: object[],
 *   inventoryWarehouseLocations: object[],
 *   productInventoryByLocation: object[],
 *   reload: () => void
 * }}
 */
export const useInventoryModalData = (productId) => {
  const [isLoading, setIsLoading] = useState(true);
  const [inventoryWarehouses, setInventoryWarehouses] = useState([]);
  const [inventoryWarehouseLocations, setInventoryWarehouseLocations] = useState([]);
  const [productInventoryByLocation, setProductInventoryByLocation] = useState([]);
  const cancelRef = useRef(null);

  const loadData = useCallback(() => {
    // Cancel any in-flight requests before starting new ones
    cancelRef.current?.();

    setIsLoading(true);

    const warehousesPromise = getInventoryWarehouses();
    const locationsPromise = getInventoryWarehouseLocations();
    const inventoryPromise = getInventoryLocationsForProduct(productId);

    cancelRef.current = () => {
      warehousesPromise?.cancel?.();
      locationsPromise?.cancel?.();
      inventoryPromise?.cancel?.();
    };

    Promise.all([warehousesPromise.promise, locationsPromise.promise, inventoryPromise.promise])
      .then(([warehousesResponse, locationsResponse, inventoryResponse]) => {
        const warehouseData = warehousesResponse.data ?? [];
        setInventoryWarehouses(warehouseData);

        const locationsData = locationsResponse.data ?? [];
        const locationsWithNames = locationsData.map((loc) => ({
          ...loc,
          name: locationNameFromParts(loc),
        }));
        setInventoryWarehouseLocations(locationsWithNames);

        const inventoryData = inventoryResponse.data ?? [];
        const inventoryWithNames = inventoryData.map((inv) => {
          const warehouse = warehouseData.find((w) => w.inventoryWarehouseId === inv.inventoryWarehouseId);
          return {
            ...inv,
            warehouseName: warehouse?.name ?? '',
            location: locationNameFromParts(inv),
          };
        });
        setProductInventoryByLocation(inventoryWithNames);

        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [productId]);

  useEffect(() => {
    loadData();
    return () => cancelRef.current?.();
  }, [loadData]);

  return {
    isLoading,
    inventoryWarehouses,
    inventoryWarehouseLocations,
    productInventoryByLocation,
    reload: loadData,
  };
};
